import { TILE } from './constants';
import { addExplored, visibleMask } from './vision';
import { getMap } from '../content/maps';
import type { MapId } from '../content/maps';
import { YARD_QUEUE } from '../content/production';
import { dist2, isqrt } from '../lib/intmath';
import { unitDef } from '../content/units';
import { terrainDef } from '../content/terrain';
import { MAX_SEARCHES_PER_TICK, findPath, isWalkable } from './path';
import type { Grid } from './path';
import { assignFormation } from './formation';
import { separateUnits } from './separation';
import { compareCommands, isLegal } from './commands';
import type { Command } from './commands';
import {
    acquireTarget,
    advanceProjectiles,
    applyImpact,
    fire,
    inWeaponRange,
    weaponOf,
    canTarget,
} from './combat';
import type { CombatUnit } from './combat';
import { growOre, isHarvester, stepHarvester } from './harvest';
import {
    ProductionKind,
    advanceQueue,
    canPlace,
    cancelHead,
    producerCount,
    hasProducerFor,
    queueIndexOf,
    powerOf,
    prerequisitesMet,
    productionBuilding,
    productionRatePct,
    spawnPointNear,
} from './build';
import { buildingDef } from '../content/buildings';
import { EventKind, LEASH_DISTANCE, Phase, UnitMode } from './types';
import type {
    Building,
    GameState,
    PlayerState,
    ProductionItem,
    Projectile,
    SimEvent,
    Unit,
    UnitId,
} from './types';

/*
 * הפונקציה היחידה שמזיזה את המשחק קדימה.
 *
 * **סדר המערכות קבוע ומתועד. שינוי הסדר משנה תוצאות ושובר כל replay ושמירה קיימים.**
 *
 *   1. פקודות        — קובעות יעדים, מטרות ועמדות
 *   2. מסלולים       — פותרות בקשות עד גבול התקציב
 *   3. תנועה         — היחידות מתקדמות לאורך המסלול
 *   4. הפרדה         — יחידות שחופפות נדחפות זו מזו
 *   5. רכישת מטרה    — מי שאין לו מטרה חוקית מחפש אחת
 *   6. ירי           — מי שבטווח ומצונן יורה
 *   7. קליעים        — תנועה, פגיעות ונזק
 *   8. מוות ופינוי   — יחידות עם hp<=0 יורדות מהלוח
 *   9. ניצחון
 *  10. tick++
 *
 * הפונקציה טהורה: אותו (state, commands, grid) תמיד מחזיר את אותו state חדש, והמצב
 * הנכנס אינו משתנה. `step` בונה טיוטות משתנות בפנים ומחזיר אובייקטים חדשים בחוץ.
 */

function toDraft(unit: Unit): CombatUnit {
    return {
        id: unit.id,
        owner: unit.owner,
        type: unit.type,
        x: unit.x,
        y: unit.y,
        hp: unit.hp,
        mode: unit.mode,
        goalTile: unit.goalTile,
        path: unit.path as number[],
        homeX: unit.homeX,
        homeY: unit.homeY,
        targetId: unit.targetId,
        cooldown: unit.cooldown,
        burstLeft: unit.burstLeft,
        cargo: unit.cargo,
        buildingType: -1,
    };
}

function freeze(draft: CombatUnit): Unit {
    return {
        id: draft.id,
        owner: draft.owner,
        type: draft.type,
        x: draft.x,
        y: draft.y,
        hp: draft.hp,
        mode: draft.mode,
        goalTile: draft.goalTile,
        path: draft.path,
        homeX: draft.homeX,
        homeY: draft.homeY,
        targetId: draft.targetId,
        cooldown: draft.cooldown,
        burstLeft: draft.burstLeft,
        cargo: draft.cargo,
    };
}

/*
 * מאגר עבודה משותף למסכת הראייה.
 *
 * מסכה חדשה לכל שחקן בכל טיק היא 18KB אשפה עשרים פעם בשנייה, בלי שום שימוש
 * אחרי הטיק. אותו דפוס כמו מאגר העבודה של `path.ts`, ובטוח מאותה סיבה:
 * ‏`visibleMask` **ממלאת אותו באפסים לפני כל שימוש**, ולכן שום שארית לא עוברת
 * בין קריאות ותוצאת הפונקציה תלויה אך ורק בקלט שלה.
 */
let visionScratch: Uint8Array = new Uint8Array(0);

/**
 * כמה פריטים ממתינים בתור אחד. **לכל תור בנפרד** — כלומר עשרה בקסרקטין ועוד
 * עשרה במפעל, ולא עשרה לכל היחידות יחד.
 *
 * עלה מ-6 לבקשת בר (10.08.2026). ‏RA2 עוצר בדיוק שם, וזה גם המספר שמאפשר
 * להזמין גל שלם בהקשות חוזרות — **בלי מנגנון "הקשה ארוכה = כמה"**, שנשקל
 * ונפסל: מגע ממושך הוא כבר בחירת קופסה, ופקודה שתלויה במשך הלחיצה היא בדיוק
 * הסוג שנקלט בטעות.
 */
const QUEUE_LIMIT = 10;

/**
 * תיקון: כמה hp בטיק, ובאיזה מחיר.
 *
 * המחיר נגזר מעלות המבנה חלקי מחלק קבוע, כלומר תיקון מלא עולה בערך רבע מהבנייה
 * מחדש — זול מספיק כדי שיהיה מהלך, יקר מספיק כדי שלא יהיה ברירת מחדל.
 */
const REPAIR_HP_PER_TICK = 2;
const REPAIR_COST_DIVISOR = 900;

/** כל כמה טיקים מתעדכן זיכרון המפה. ראו ההערה במקום השימוש. */
const EXPLORE_INTERVAL_TICKS = 4;

export function step(state: GameState, commands: readonly Command[], grid: Grid): GameState {
    const drafts = state.units.map(toDraft);
    const byId = new Map<UnitId, CombatUnit>(drafts.map((d) => [d.id, d]));
    const pathQueue = [...state.pathQueue];
    const events: SimEvent[] = [];
    let nextEntityId = state.nextEntityId;

    /*
     * טיוטות מבנים: `Building` הוא readonly בחוץ, אבל בפנים `step` בונה טיוטות
     * שאפשר לכתוב אליהן — בדיוק כמו ביחידות.
     */
    const buildings: BuildingDraft[] = state.buildings.map((b) => ({ ...b }));
    const spawnedUnits: Unit[] = [];
    /*
     * טיוטות שחקנים: הפקודות הכלכליות פועלות עליהן ישירות, במקום להצטבר לרשימות
     * ולהיות מוחלות מאוחר יותר. פחות מצב ביניים = פחות מקומות להתבלבל בהם.
     */
    const playerDrafts: PlayerDraft[] = state.players.map((p) => ({
        ...p,
        queues: p.queues.map((queue) => [...queue]),
    }));

    nextEntityId = applyCommands(
        state,
        grid,
        commands,
        byId,
        pathQueue,
        buildings,
        playerDrafts,
        nextEntityId,
    );
    resolvePaths(grid, byId, pathQueue);
    moveUnits(grid, drafts);
    /*
     * ההפרדה באה **אחרי** התנועה ולא במקומה: המסלול קובע לאן הולכים, וההפרדה רק
     * מונעת מכולם לעמוד על אותה נקודה בדרך. הפוך מזה — לחסום תנועה לתוך אריח
     * תפוס — היה מייצר פקקים בכל קבוצה.
     */
    separateUnits(drafts, grid);

    /*
     * ---- כלכלה
     *
     * שכבת העפרות משותפת בהפניה כל עוד איש לא כרה — העתקה של אלפי ערכים בכל טיק
     * היא בזבוז. ברגע שקוצר אחד כרה, המערך מועתק **פעם אחת** באותו טיק, וכל שאר
     * הקוצרים כותבים לעותק. ההעתקה יושבת בתוך `oreAccess.write` ולא לפני הקריאה:
     * קודם היא הייתה מותנית רק בקיומו של קוצר חי, כלומר קרתה תמיד.
     */
    let ore = state.ore;
    const oreAccess = {
        read: () => ore,
        write: () => {
            if (ore === state.ore) ore = Uint16Array.from(state.ore);
            return ore;
        },
    };
    /*
     * העפרות צומחות לפני הכרייה: מחזור הצמיחה נופל על טיק קבוע, ואם הוא היה
     * אחרי הכרייה, אריח שנכרה עד אפס באותו טיק היה מקבל את הצמיחה מיד — כלומר
     * הכרייה הייתה חינם פעם ב-100 טיקים.
     */
    growOre(state.tick, getMap(state.mapId as MapId).ore, oreAccess);

    const earned = new Map<number, number>();
    for (const unit of drafts) {
        if (unit.hp <= 0 || !isHarvester(unit)) continue;
        const outcome = stepHarvester(unit, oreAccess, buildings, grid, pathQueue);
        if (outcome.credits > 0) {
            earned.set(unit.owner, (earned.get(unit.owner) ?? 0) + outcome.credits);
        }
    }

    /*
     * ---- קרב
     *
     * מבנים משתתפים דרך מתאם שנראה כמו יחידה. זה מה שמאפשר שלושה דברים בבת אחת
     * ובלי כפילות קוד: מגדל הגנה יורה, יחידות יכולות להרוס בסיס, ונזק היקפי פוגע
     * גם במבנים. אחרי הקרב ה-hp מוחזר למבנים עצמם.
     */
    const buildingUnits: CombatUnit[] = buildings.map((building) => {
        const def = buildingDef(building.type);
        return {
            id: building.id,
            owner: building.owner,
            type: 0,
            x: building.tileX * TILE + (def.w * TILE) / 2,
            y: building.tileY * TILE + (def.h * TILE) / 2,
            hp: building.hp,
            mode: UnitMode.Holding,
            goalTile: -1,
            path: [],
            homeX: 0,
            homeY: 0,
            targetId: building.targetId,
            cooldown: building.cooldown,
            burstLeft: building.burstLeft,
            cargo: 0,
            buildingType: building.type,
        };
    });
    const combatants: CombatUnit[] = [...drafts, ...buildingUnits];
    for (const entity of combatants) byId.set(entity.id, entity);

    // ---- רכישת מטרה, ירי
    const spawned: Projectile[] = [];
    /*
     * **נזק מיידי נצבר ומוחל בבת אחת בסוף הסבב, ולא תוך כדי.**
     *
     * החלת הנזק במקום נתנה יתרון ירייה-ראשונה למי שהמזהה שלו נמוך יותר: הוא
     * מופיע קודם בלולאה, ומי שמת מהירייה שלו כבר לא הספיק לירות באותו טיק.
     * `simBatch` מדד את זה — שני יריבים זהים על מפה סימטרית, ועמדה 1 ניצחה
     * 8 מתוך 8. ירי בטיק אחד הוא סימולטני, ולכן גם מי שמת בטיק הזה יורה בו.
     */
    const instantDamage = new Map<UnitId, number>();
    for (const unit of combatants) {
        if (unit.hp <= 0) continue;
        if (unit.cooldown > 0) unit.cooldown--;

        const weaponId = weaponOf(unit);
        if (weaponId === null) continue;
        // קוצר בדרך לעבודה לא נעצר לחפש מטרות — אין לו נשק בכלל.

        let target = unit.targetId >= 0 ? byId.get(unit.targetId) : undefined;
        /*
         * ‏`canTarget` נבדק גם על מטרה **קיימת** ולא רק ברכישה: פקודת תקיפה
         * מפורשת יכולה לכוון טנק אל מסוק, ובלי הבדיקה הזו הוא היה רודף אותו
         * לנצח בלי לירות — התנהגות שנראית כמו יחידה תקועה.
         */
        if (!target || target.hp <= 0 || target.owner === unit.owner || !canTarget(weaponId, target)) {
            unit.targetId = acquireTarget(unit, combatants, weaponId);
            target = unit.targetId >= 0 ? byId.get(unit.targetId) : undefined;
        }
        if (!target) continue;

        if (inWeaponRange(unit, target, weaponId)) {
            if (unit.cooldown === 0) {
                const result = fire(unit, target, weaponId, nextEntityId, events);
                if (result.projectile) {
                    spawned.push(result.projectile);
                    nextEntityId++;
                } else if (result.instantDamage > 0) {
                    instantDamage.set(
                        target.id,
                        (instantDamage.get(target.id) ?? 0) + result.instantDamage,
                    );
                }
            }
            continue;
        }

        /*
         * המטרה מחוץ לטווח — האם לרדוף?
         *
         *   Holding    → לעולם לא. זו כל המשמעות של "עצור".
         *   Attacking  → תמיד. השחקן ביקש במפורש לתקוף את היחידה הזו.
         *   AttackMove → כן, אבל היעד המקורי נשמר וחוזרים אליו אחרי החיסול.
         *   Moving     → לא. יורים תוך כדי, אבל לא סוטים מהמסלול.
         *   Idle       → **רק עד גבול הרצועה מהעוגן.** זה מה שמונע ממשמר להיגרר
         *                אחרי צופה בודד ולנטוש את מה שהוא שומר עליו — הפתרון של
         *                RA2, במקום בורר עמדות.
         */
        if (unit.mode === UnitMode.Holding || unit.mode === UnitMode.Moving) continue;

        if (unit.mode === UnitMode.Idle) {
            /*
             * הרצועה נמדדת **למיקום היחידה עצמה**, לא למיקום המטרה.
             *
             * בגרסה הראשונה מדדתי למטרה, וזה שבר את התכונה לגמרי: הרצועה (4 אריחים)
             * קטנה מטווח הנשק (5), ולכן כל מטרה "בתוך הרצועה" הייתה ממילא בטווח
             * ירי — והיחידה לא רדפה אף פעם. השאלה הנכונה היא כמה היחידה מוכנה
             * להתרחק מהעמדה שלה, לא איפה האויב עומד.
             */
            const strayed = dist2(unit.x, unit.y, unit.homeX, unit.homeY);
            if (strayed > LEASH_DISTANCE * LEASH_DISTANCE) {
                // הגענו לקצה הרצועה: מוותרים על המטרה וחוזרים לעמדה.
                unit.targetId = -1;
                unit.goalTile =
                    Math.floor(unit.homeY / TILE) * grid.w + Math.floor(unit.homeX / TILE);
                unit.path = [];
                if (!pathQueue.includes(unit.id)) pathQueue.push(unit.id);
                continue;
            }
        }

        // בקשת מסלול רק כשאין מסלול פעיל — בקשה בכל טיק הייתה מציפה את תקציב ה-A*.
        if (unit.path.length === 0) {
            unit.goalTile = Math.floor(target.y / TILE) * grid.w + Math.floor(target.x / TILE);
            if (!pathQueue.includes(unit.id)) pathQueue.push(unit.id);
        }
    }

    // כל היריות של הטיק נורו — עכשיו הן נוחתות, כולן יחד.
    for (const [id, damage] of instantDamage) {
        const target = byId.get(id);
        if (target) target.hp -= damage;
    }

    // ---- קליעים
    const alive = [...state.projectiles, ...spawned];
    const { survivors, impacts } = advanceProjectiles(alive, byId, events);
    for (const impact of impacts) applyImpact(impact, combatants);

    // מחזירים את ה-hp מהמתאמים אל המבנים עצמם, ומדווחים על מבנה שנהרס.
    buildingUnits.forEach((adapter, index) => {
        const building = buildings[index];
        if (!building) return;
        if (adapter.hp <= 0 && building.hp > 0) {
            events.push({
                kind: EventKind.Death,
                x: adapter.x,
                y: adapter.y,
                a: building.type,
                b: building.owner,
            });
        }
        building.hp = adapter.hp;
        building.targetId = adapter.targetId;
        building.cooldown = adapter.cooldown;
        building.burstLeft = adapter.burstLeft;
    });

    // ---- מוות ופינוי
    const living: CombatUnit[] = [];
    for (const unit of drafts) {
        if (unit.hp > 0) {
            living.push(unit);
            continue;
        }
        events.push({ kind: EventKind.Death, x: unit.x, y: unit.y, a: unit.type, b: unit.owner });
        // המונה נספר כאן ולא מהאירועים: אירועים הם פלט לרנדרר ואינם נשמרים.
        const owner = playerDrafts[unit.owner];
        if (owner) owner.unitsLost++;
    }
    /*
     * מטרה שמתה מתנקה מיד, אחרת יחידות מכוונות אל מזהה שכבר לא קיים.
     *
     * **המבנים חייבים להיות כאן.** יחידות ומבנים חולקים מרחב מזהים אחד
     * (`nextEntityId`), ולכן קבוצה שנבנתה מהיחידות בלבד "הרגה" כל מטרה שהיא
     * מבנה — בכל טיק. התוצאה לא נראתה כמו באג אלא כמו איזון גרוע: הטנק רכש
     * מבנה, איפס אותו בסוף הטיק, בחר בטיק הבא את מה שקרוב **עכשיו**, וכך כל
     * ירייה (אחת ל-1.6 שניות) נחתה במבנה אחר. הנזק התפזר על הבסיס כולו ושום
     * מבנה לא נפל — בדיוק חוסר ההכרעה של מלכודת 24.
     */
    const livingIds = new Set<number>();
    for (const unit of living) livingIds.add(unit.id);
    for (const building of buildings) if (building.hp > 0) livingIds.add(building.id);
    for (const unit of living) {
        if (unit.targetId >= 0 && !livingIds.has(unit.targetId)) {
            unit.targetId = -1;
            // המשימה הושלמה: חוזרים למצב רגיל, והעמדה החדשה היא המקום הנוכחי.
            if (unit.mode === UnitMode.Attacking) {
                unit.mode = UnitMode.Idle;
                unit.homeX = unit.x;
                unit.homeY = unit.y;
            }
        }
    }

    /*
     * ---- ייצור
     *
     * מנוקז אחרי הקרב ולפני בדיקת הניצחון: מבנה שנהרס בטיק הזה כבר לא מספק חשמל,
     * ופריט שהושלם בטיק הזה עדיין לא מציל שחקן שכל הבסיס שלו נפל.
     */
    const afterProduction = playerDrafts.map((player) => {
        const rate = productionRatePct(powerOf(buildings, player.index));
        let credits = player.credits + (earned.get(player.index) ?? 0);

        /*
         * כל מבנה ייצור נוסף מזרז את התור שלו. זה מה שהופך "לבנות עוד מפעל"
         * להחלטה כלכלית, ומה שמונע את החנק שנמדד: אשראי שנצבר בלי דרך להוציא אותו.
         */
        /*
         * **כל תור מתקדם בנפרד ובאותו טיק** — זה מה שהופך את הקסרקטין והמפעל
         * לייצור מקביל אמיתי. הם חולקים ארנק אחד, ולכן הסדר קובע מי מנוקז קודם
         * כשאין מספיק אשראי: סדר התורים ב-`content/production.ts`, שהוא קבוע.
         */
        const queues = player.queues.map((queue) => {
            /*
             * **תור של מבנה שנהרס אינו מנוקז.** ‏`producerCount` מחזיר לפחות 1,
             * ולכן קסרקטין שנהרס באמצע אימון המשיך לשאוב מהארנק — והחייל
             * "הושלם" בלי שיוכל להיוולד, כי אין ממה. הכסף נעלם בשקט.
             */
            if (queue[0] && !hasProducerFor(buildings, player.index, queue[0])) {
                return queue;
            }
            const outcome = advanceQueue(
                queue,
                credits,
                rate,
                producerCount(buildings, player.index, queue[0]),
            );
            credits -= outcome.spent;
            return outcome.queues as ProductionItem[];
        });

        /*
         * ---- תיקון מבנים
         *
         * **כאן ולא לפני הקרב ולא אחרי הייצור.** לפני הקרב, מבנה היה מרפא נזק
         * שעוד לא ספג באותו טיק; אחרי הייצור, הוא היה מתחרה על אשראי שכבר נוקז.
         * ליד ניקוז הייצור הוא משתתף באותו ארנק ובאותו סדר — וזה מקובע לנצח,
         * כי סדר המערכות שובר כל שמירה ו-replay.
         */
        for (const building of buildings) {
            if (building.owner !== player.index || building.repairing !== 1) continue;
            /*
             * **מבנה שכבר מת אינו מתוקן.**
             *
             * סדר המערכות בטיק הוא מוות → תיקון → פינוי המתים, ולכן בלי הבדיקה
             * הזו מבנה שה-hp שלו נחת על 0 קיבל כאן ‎+2 ו**שרד את הפינוי** — אחרי
             * שאירוע המוות שלו כבר נורה. השחקן ראה פיצוץ, שמע את צליל ההרס וקיבל
             * את ההתראה, והמבנה נשאר עומד. חלון החשיפה הוא `REPAIR_HP_PER_TICK`
             * מתוך גודל המכה, כלומר נדיר — ולכן גם בלתי ניתן לשחזור כשמדווחים עליו.
             */
            if (building.hp <= 0) continue;
            const def = buildingDef(building.type);
            if (building.hp >= def.hp) {
                // הגיע למלא — הסימון נכבה מעצמו, אחרת הוא שואב לנצח.
                building.repairing = 0;
                continue;
            }
            const cost = Math.max(1, Math.floor(def.cost / REPAIR_COST_DIVISOR));
            if (credits < cost) continue;
            credits -= cost;
            building.hp = Math.min(def.hp, building.hp + REPAIR_HP_PER_TICK);
        }

        return { ...player, credits, queues };
    });

    /*
     * יחידה שהושלמה יוצאת מהמפקדה מיד. מבנה שהושלם **ממתין להצבה** של השחקן —
     * זו הצורה של RA2, והיא מה שהופך את מיקום הבסיס להחלטה.
     */
    const withSpawns = afterProduction.map((player) => {
        /*
         * **כל תור יחידות עצמאי, ולכן כולם נבדקים.** קודם נבדק תור אחד בלבד;
         * עם קסרקטין ומפעל שרצים במקביל, טנק שהיה מוכן היה מחכה לתורו אחרי החי"ר
         * במקום לצאת — כלומר המקביליות הייתה מתבטלת בשורה האחרונה.
         */
        let current = player;
        for (let index = 0; index < current.queues.length; index++) {
            const spawned = spawnFromQueue(current, index, buildings, grid, state);
            if (spawned) current = spawned;
        }
        return current;
    });

    /** מוציא יחידה מוכנה מתור אחד, או `null` אם אין מה להוציא. */
    function spawnFromQueue(
        player: PlayerDraft,
        index: number,
        buildings: BuildingDraft[],
        grid: Grid,
        state: GameState,
    ): PlayerDraft | null {
        const queue = player.queues[index];
        const head = queue?.[0];
        if (!queue || !head || !head.ready) return null;
        if (head.kind !== ProductionKind.Unit) return null;
        const source = productionBuilding(buildings, player, head.type);
        if (!source) return null;
        const spot = spawnPointNear({ ...state, buildings }, grid, source);
        if (!spot) return null;

        const def = unitDef(head.type);
        /*
         * נקודת התכנסות: יחידה שנולדה יוצאת אליה מיד.
         *
         * **זה מה שהופך את הייצור למשהו שאפשר להפעיל ולשכוח.** בלעדיה כל יחידה
         * נשארת עומדת בפתח המפעל עד שהשחקן שם לב אליה, וכוח שנבנה תוך כדי קרב
         * מגיע לחזית באיחור של דקה.
         */
        const rally = source.rallyTile;
        const hasRally = rally >= 0;
        if (hasRally) pathQueue.push(nextEntityId);

        /*
         * יחידה שיצאה היא אירוע. בלי זה השחקן שהזמין טנק, סגר את הפאנל וחזר
         * לקרב לא מקבל שום סימן שהוא הגיע — לא צליל ולא הודעה.
         */
        events.push({
            kind: EventKind.Spawned,
            x: spot.x,
            y: spot.y,
            a: head.type,
            b: player.index,
        });
        spawnedUnits.push({
            id: nextEntityId++,
            owner: player.index,
            type: head.type,
            x: spot.x,
            y: spot.y,
            hp: def.hp,
            mode: hasRally ? UnitMode.Moving : UnitMode.Idle,
            goalTile: hasRally ? rally : -1,
            path: [],
            homeX: spot.x,
            homeY: spot.y,
            targetId: -1,
            cooldown: 0,
            burstLeft: 0,
            cargo: 0,
        });
        return {
            ...player,
            unitsBuilt: player.unitsBuilt + 1,
            queues: player.queues.map((q, i) => (i === index ? q.slice(1) : q)),
        };
    }

    // ---- ניצחון
    const players = withSpawns.map((player) => {
        // שחקן מובס כשאין לו יחידות **ולא** מבנים — בסיס שלם הוא עדיין משחק.
        const hasUnits = living.some((unit) => unit.owner === player.index);
        const hasBuildings = buildings.some(
            (building) => building.owner === player.index && building.hp > 0,
        );
        const defeated = !hasUnits && !hasBuildings;
        return defeated === player.defeated ? player : { ...player, defeated };
    });
    const standing = players.filter((player) => !player.defeated);
    const ended = state.phase === Phase.Ended || standing.length <= 1;

    /*
     * ---- ערפל קרב
     *
     * **אחרי הכול**, כי מה שנחשף תלוי במיקומים הסופיים של הטיק — יחידה שזזה
     * וחשפה אזור, ומבנה שנהרס ולכן כבר לא חושף. רק הזיכרון נשמר; מה שנראה עכשיו
     * נגזר מחדש בכל פעם, וראו `sim/vision.ts` למה זו החלטה ולא עצלות.
     */
    const nextState = {
        ...state,
        units: [...living.map(freeze), ...spawnedUnits],
        buildings: buildings.filter((b) => b.hp > 0),
    };
    const tiles = grid.w * grid.h;
    if (visionScratch.length !== tiles) visionScratch = new Uint8Array(tiles);
    /*
     * **לא בכל טיק.** סריקת הראייה של כל היחידות והמבנים לשני השחקנים היא העבודה
     * היקרה ביותר בטיק, ומדדנו: היא שילשה את זמן הריצה של 5,000 טיקים. פעם ב-4
     * טיקים היא 200ms של פיגור בזיכרון המפה — בלתי מורגש, כי מה שנראה **עכשיו**
     * מחושב בנפרד בכל פריים ברנדרר. התנאי תלוי בטיק בלבד ולכן דטרמיניסטי.
     */
    const nextTick = state.tick + 1;
    const withVision =
        nextTick % EXPLORE_INTERVAL_TICKS !== 0
            ? players
            : players.map((player) => {
                  const seen = visibleMask(
                      nextState,
                      player.index,
                      grid.w,
                      grid.h,
                      visionScratch,
                  );
                  const next = addExplored(player.explored, seen);
                  // אותה הפניה כשלא התגלה כלום — הרנדרר משווה הפניה כדי לדעת
                  // אם לצייר מחדש את שכבת הערפל.
                  return next === player.explored ? player : { ...player, explored: next };
              });

    return {
        tick: nextTick,
        rng: state.rng,
        seed: state.seed,
        nextEntityId,
        mapId: state.mapId,
        // נקבע פעם אחת בפתיחה ולא משתנה — אבל עובר הלאה, אחרת הוא נעלם בטיק הראשון.
        startSlots: state.startSlots,
        ore,
        units: nextState.units,
        buildings: nextState.buildings,
        projectiles: survivors,
        players: withVision,
        phase: ended ? Phase.Ended : Phase.Playing,
        winner: ended ? (standing[0]?.index ?? -1) : -1,
        /*
         * גם היחידות שנולדו בטיק הזה. `livingIds` נבנה מהטיוטות שקיימות מתחילת
         * הטיק, ולכן יחידה שיצאה זה עתה עם נקודת התכנסות נשרה מהתור מיד וביקשה
         * מסלול שלעולם לא נפתר — היא פשוט נשארה עומדת.
         */
        pathQueue: pathQueue.filter(
            (id) => livingIds.has(id) || spawnedUnits.some((unit) => unit.id === id),
        ),
        events,
    };
}

type BuildingDraft = {
    -readonly [K in keyof Building]: Building[K];
};

export interface PlayerDraft {
    index: number;
    kind: PlayerState['kind'];
    credits: number;
    defeated: boolean;
    difficulty: number;
    /** נשאר בטיוטה כדי שלא ילך לאיבוד; מתעדכן רק בסוף הטיק, ב"ערפל קרב". */
    explored: Uint8Array;
    unitsBuilt: number;
    unitsLost: number;
    queues: ProductionItem[][];
}

function applyCommands(
    state: GameState,
    grid: Grid,
    commands: readonly Command[],
    byId: Map<UnitId, CombatUnit>,
    pathQueue: UnitId[],
    buildings: BuildingDraft[],
    players: PlayerDraft[],
    nextEntityId: number,
): number {
    // מיון לסדר מלא לפני ביצוע — סדר ההגעה של הפקודות לא יקבע את התוצאה.
    const ordered = [...commands].sort(compareCommands);

    for (const command of ordered) {
        if (!isLegal(state, grid, command)) continue;

        // ---- פקודות כלכלה: פועלות על השחקן, לא על יחידות
        const draft = players[command.player];

        if (command.kind === 'queue') {
            if (!draft) continue;
            const item = { kind: command.itemKind as ProductionKind, type: command.itemType };
            if (!prerequisitesMet(item, buildings, command.player)) continue;
            /*
             * **המגבלה היא לכל תור בנפרד.** קודם היה תור יחידות אחד, ולכן שישה
             * טנקים בתור חסמו הזמנת חייל — וזו הייתה תלונה של בר. עכשיו לקסרקטין
             * ולמפעל יש כל אחד את ששת המקומות שלו.
             */
            const queue = draft.queues[queueIndexOf(item)];
            if (!queue) continue;
            // תור חסום: פריט מוכן שממתין להצבה עוצר את מה שאחריו, כמו ב-RA2.
            if (queue.length >= QUEUE_LIMIT) continue;
            queue.push({ kind: item.kind, type: item.type, paid: 0, ready: false });
            continue;
        }

        if (command.kind === 'cancel') {
            if (!draft) continue;
            const queue = draft.queues[command.queueIndex];
            if (!queue) continue;
            const result = cancelHead(queue);
            queue.length = 0;
            queue.push(...result.queue);
            draft.credits += result.refund;
            continue;
        }

        if (command.kind === 'place') {
            if (!draft) continue;
            const head = draft.queues[YARD_QUEUE]?.[0];
            if (!head || !head.ready) continue;
            if (!canPlace({ ...state, buildings }, grid, command.player, head.type, command.tileX, command.tileY)) {
                continue;
            }
            buildings.push({
                id: nextEntityId++,
                owner: command.player,
                type: head.type,
                tileX: command.tileX,
                tileY: command.tileY,
                hp: buildingDef(head.type).hp,
                rallyTile: -1,
                repairing: 0,
                targetId: -1,
                cooldown: 0,
                burstLeft: 0,
            });
            draft.queues[YARD_QUEUE]?.shift();
            continue;
        }

        if (command.kind === 'setRally') {
            const target = buildings.find((b) => b.id === command.buildingId);
            if (target) target.rallyTile = command.tileY * grid.w + command.tileX;
            continue;
        }

        if (command.kind === 'repair') {
            if (!draft) continue;
            const target = buildings.find((b) => b.id === command.buildingId);
            // מתג: אותה פקודה מדליקה ומכבה, כמו ב-RA2.
            if (target) target.repairing = target.repairing === 1 ? 0 : 1;
            continue;
        }

        if (command.kind === 'sell') {
            if (!draft) continue;
            const index = buildings.findIndex((b) => b.id === command.buildingId);
            const target = buildings[index];
            if (index >= 0 && target) {
                // מחצית מהעלות חוזרת — מספיק כדי שמכירה תהיה מהלך, ולא עונש.
                draft.credits += Math.floor(buildingDef(target.type).cost / 2);
                buildings.splice(index, 1);
            }
            continue;
        }

        // סדר קבוע לפי מזהה, כדי שהשיבוץ לתצורה יהיה זהה בכל הרצה.
        const units = command.unitIds
            .map((id) => byId.get(id))
            .filter((u): u is CombatUnit => u !== undefined)
            .sort((a, b) => a.id - b.id);
        if (units.length === 0) continue;

        switch (command.kind) {
            case 'move':
            case 'attackMove': {
                const targets = assignFormation(grid, command.tileX, command.tileY, units.length);
                units.forEach((unit, index) => {
                    unit.goalTile = targets[index] ?? targets[0] ?? -1;
                    unit.path = [];
                    unit.mode = command.kind === 'move' ? UnitMode.Moving : UnitMode.AttackMove;
                    // פקודת תנועה מנתקת מטרה: השחקן אמר לאן ללכת, לא במי לירות.
                    unit.targetId = -1;
                    if (!pathQueue.includes(unit.id)) pathQueue.push(unit.id);
                });
                break;
            }
            case 'attack': {
                for (const unit of units) {
                    unit.targetId = command.targetId;
                    // תקיפה מפורשת רודפת בלי רצועה — השחקן ביקש.
                    unit.mode = UnitMode.Attacking;
                    unit.path = [];
                    unit.goalTile = -1;
                }
                break;
            }
            case 'stop': {
                for (const unit of units) {
                    unit.path = [];
                    unit.goalTile = -1;
                    unit.targetId = -1;
                    unit.mode = UnitMode.Holding;
                    // עצירה קובעת עוגן חדש: מכאן נמדדת הרצועה אם יחזרו למצב רגיל.
                    unit.homeX = unit.x;
                    unit.homeY = unit.y;
                }
                break;
            }
        }
    }

    return nextEntityId;
}

function resolvePaths(grid: Grid, byId: Map<UnitId, CombatUnit>, pathQueue: UnitId[]): void {
    let searches = 0;
    while (pathQueue.length > 0 && searches < MAX_SEARCHES_PER_TICK) {
        const id = pathQueue.shift();
        if (id === undefined) break;
        const unit = byId.get(id);
        if (!unit || unit.goalTile < 0) continue;
        /*
         * **מטוס לא מחפש מסלול — הוא טס.** הוא גם לא נספר בתקציב החיפושים, וזה
         * לא רק חיסכון: A* על רשת הקרקע היה מסרב לשלוח אותו מעל מים או סלע,
         * כלומר הופך את היתרון היחיד שלו לחיסרון.
         */
        if (isAir(unit)) continue;

        searches++;

        const goalX = unit.goalTile % grid.w;
        const goalY = (unit.goalTile - goalX) / grid.w;
        const startX = Math.floor(unit.x / TILE);
        const startY = Math.floor(unit.y / TILE);

        const result = findPath(grid, startX, startY, goalX, goalY);
        if (result) {
            /*
             * **המסלול נקבע כאן; המצב לא.** בגרסה הקודמת השורה הזו כתבה
             * `mode = Moving` בכל פעם שנמצא מסלול — שריד מהתקופה שבה היו רק שני
             * מצבים — והיא מחקה `Attacking` ו-`AttackMove` ברגע שהיחידה התחילה לזוז.
             * המצב שייך לשכבת הפקודות, לא לפותר המסלולים.
             */
            unit.path = result.tiles;
        } else {
            // כישלון נקי: היחידה נעצרת במקום להתחיל לרעוד לכיוון יעד בלתי אפשרי.
            unit.path = [];
            unit.goalTile = -1;
            if (unit.mode === UnitMode.Moving || unit.mode === UnitMode.AttackMove) {
                unit.mode = UnitMode.Idle;
            }
            /*
             * **מטרה שאין אליה דרך משתחררת.** בלי זה יחידה ב-`Attacking` מבקשת
             * מסלול חדש בכל טיק, נכשלת, ומבקשת שוב — וכל בקשה כזו שורפת אחד
             * משמונת החיפושים בטיק ומרעיבה את הקוצרים ואת שאר הכוח.
             */
            if (unit.mode === UnitMode.Attacking) {
                unit.mode = UnitMode.Idle;
                unit.targetId = -1;
                unit.homeX = unit.x;
                unit.homeY = unit.y;
            }
        }
    }
}

/** יחידת אוויר. מבנה הוא תמיד קרקע. */
function isAir(unit: CombatUnit): boolean {
    return unit.buildingType < 0 && unitDef(unit.type).layer === 'air';
}

/**
 * תנועת אוויר: קו ישר אל מרכז אריח היעד, בלי מסלול ובלי טרשת.
 *
 * המהירות אינה מושפעת מהקרקע שמתחת — זה **מה שמייחד** את הציר האווירי, ולא
 * בונוס מהירות ששכחנו לכייל.
 */
function flyUnit(grid: Grid, unit: CombatUnit): void {
    if (unit.goalTile < 0) {
        if (unit.mode === UnitMode.Moving || unit.mode === UnitMode.AttackMove) {
            unit.mode = UnitMode.Idle;
        }
        return;
    }
    const gx = unit.goalTile % grid.w;
    const gy = (unit.goalTile - gx) / grid.w;
    const targetX = gx * TILE + TILE / 2;
    const targetY = gy * TILE + TILE / 2;

    const dx = targetX - unit.x;
    const dy = targetY - unit.y;
    const distance = isqrt(dx * dx + dy * dy);
    const speed = unitDef(unit.type).speed;

    if (distance <= speed) {
        unit.x = targetX;
        unit.y = targetY;
        unit.goalTile = -1;
        /*
         * **`AttackMove` חייב להתאפס כאן בדיוק כמו ביחידת קרקע.** בלי זה מטוס
         * שקיבל תקוף-נוע נשאר במצב הזה לנצח: הוא רודף בלי רצועה כל אויב שנכנס
         * לטווח הראייה, וגרוע מזה — `freeArmy` של היריב מסננת `AttackMove`,
         * ולכן מסוק שיצא בגל אחד לא נספר שוב בצבא לעולם.
         */
        if (unit.mode === UnitMode.Moving || unit.mode === UnitMode.AttackMove) {
            unit.mode = UnitMode.Idle;
        }
        unit.homeX = unit.x;
        unit.homeY = unit.y;
        return;
    }
    // חלוקה שלמה בלבד — התוצאה חייבת להיות זהה בכל מנוע.
    unit.x += Math.trunc((dx * speed) / distance);
    unit.y += Math.trunc((dy * speed) / distance);
}

function moveUnits(grid: Grid, drafts: readonly CombatUnit[]): void {
    for (const unit of drafts) {
        if (isAir(unit)) {
            flyUnit(grid, unit);
            continue;
        }
        // עמדת Hold לא זזה מיוזמתה, אבל פקודת תנועה מפורשת עדיין מזיזה אותה.
        if (unit.path.length === 0) {
            if (unit.mode === UnitMode.Moving && unit.goalTile < 0) unit.mode = UnitMode.Idle;
            continue;
        }

        const def = unitDef(unit.type);
        const tileX = Math.floor(unit.x / TILE);
        const tileY = Math.floor(unit.y / TILE);
        const terrainId = isWalkable(grid, tileX, tileY)
            ? (grid.terrain[tileY * grid.w + tileX] ?? 0)
            : 0;
        // מהירות מושפעת מהקרקע שעליה היחידה עומדת עכשיו, לא מזו שאליה היא הולכת.
        let remaining = Math.max(1, Math.floor((def.speed * terrainDef(terrainId).speedPct) / 100));

        // לולאה, כי במהירות גבוהה או באריח קטן יחידה עשויה לעבור כמה אריחים בטיק.
        while (remaining > 0 && unit.path.length > 0) {
            const next = unit.path[0] ?? -1;
            if (next < 0) break;
            const nx = next % grid.w;
            const ny = (next - nx) / grid.w;
            const targetX = nx * TILE + TILE / 2;
            const targetY = ny * TILE + TILE / 2;

            const dx = targetX - unit.x;
            const dy = targetY - unit.y;
            const distance = isqrt(dx * dx + dy * dy);

            if (distance <= remaining) {
                unit.x = targetX;
                unit.y = targetY;
                unit.path = unit.path.slice(1);
                remaining -= distance;
                continue;
            }

            // חלוקה שלמה בלבד — התוצאה חייבת להיות זהה בכל מנוע.
            unit.x += Math.trunc((dx * remaining) / distance);
            unit.y += Math.trunc((dy * remaining) / distance);
            remaining = 0;
        }

        if (unit.path.length === 0) {
            /*
             * הגענו ליעד. **העוגן נקבע כאן**: מהמקום שאליו נשלחת נמדדת הרצועה,
             * לא מהמקום שבו נולדת.
             *
             * `Holding` ו-`Attacking` נשמרים: מי שנצטווה לעצור לא חוזר לרדוף רק
             * מפני שסיים מסלול, ומי שנשלח לתקוף יחידה מסוימת ממשיך לרדוף אחריה
             * גם אם היא נסוגה. `Attacking` מתאפס כשהמטרה מתה.
             */
            if (unit.mode === UnitMode.Moving || unit.mode === UnitMode.AttackMove) {
                unit.mode = UnitMode.Idle;
            }
            unit.goalTile = -1;
            unit.homeX = unit.x;
            unit.homeY = unit.y;
        }
    }
}
